import { useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, ChevronDown, Eye, Pencil, EyeOff, FileUp, FileDown, Download, Paperclip } from "lucide-react";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { ALL_LEAF_VALUES, DEFAULT_LAYOUT, leafLabel, groupLabel } from "../lib/navLayout";

// Sections offered for per-user access control (order matters for readability).
const SECTIONS = ALL_LEAF_VALUES;
const GROUPED = DEFAULT_LAYOUT.filter((n) => n.t === "group").flatMap((n) => n.children);
const PERM_GROUPS = [
  ...DEFAULT_LAYOUT.filter((n) => n.t === "group").map((n) => ({ node: n, keys: n.children.filter((k) => SECTIONS.includes(k)) })),
  { node: null, keys: SECTIONS.filter((k) => !GROUPED.includes(k)) },
];

const ACTIONS = [
  { id: "import", label: "استيراد Excel", icon: FileUp },
  { id: "export", label: "تصدير Excel / PDF", icon: FileDown },
  { id: "download", label: "تنزيل الملفات", icon: Download },
  { id: "upload", label: "إرفاق ورفع الملفات", icon: Paperclip },
];

export const ActionPerms = ({ user, onSaved }) => {
  const [blocked, setBlocked] = useState(user.action_blocked || []);
  const [saving, setSaving] = useState(false);
  const toggle = async (id, allowed) => {
    const prev = blocked;
    const next = allowed ? blocked.filter((a) => a !== id) : [...blocked, id];
    setBlocked(next);
    setSaving(true);
    try { onSaved?.(await api.setUserActions(user.email, next)); toast.success("تم حفظ الصلاحيات"); }
    catch (e) { setBlocked(prev); toast.error(e?.response?.data?.detail || "تعذّر حفظ الصلاحيات"); }
    finally { setSaving(false); }
  };
  const uid = user.user_id || user.email;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5" data-testid={`action-perms-${uid}`}>
      <p className="mb-2 text-xs font-bold text-amber-800">صلاحيات الملفات (تشمل كل الأقسام)</p>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {ACTIONS.map(({ id, label, icon: Icon }) => {
          const on = !blocked.includes(id);
          return (
            <label key={id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700">
              <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-amber-700" /> {label}</span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">{on ? "مسموح" : "ممنوع"}
                <Switch checked={on} disabled={saving} onCheckedChange={(v) => toggle(id, v)} data-testid={`action-perm-${uid}-${id}`} /></span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const ModulePermsEditor = ({ user, t, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState(user.module_perms || {});
  const [saving, setSaving] = useState(false);

  const count = Object.keys(perms).length;

  const persist = async (next) => {
    setPerms(next);
    setSaving(true);
    try {
      const updated = await api.setUserModules(user.email, next);
      onSaved?.(updated);
      toast.success("تم حفظ الصلاحيات");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر حفظ الصلاحيات");
      setPerms(user.module_perms || {});
    } finally {
      setSaving(false);
    }
  };

  const setLevel = (key, level) => {
    const next = { ...perms };
    if (level === "hidden") delete next[key];
    else next[key] = level;
    persist(next);
  };

  const showAll = () => {
    const next = {};
    SECTIONS.forEach((k) => { next[k] = "view"; });
    persist(next);
  };
  const clearAll = () => persist({});
  const editAll = () => {
    const next = {};
    SECTIONS.forEach((k) => { next[k] = "edit"; });
    persist(next);
  };
  const setGroup = (keys, level) => {
    const next = { ...perms };
    keys.forEach((k) => { if (level === "hidden") delete next[k]; else next[k] = level; });
    persist(next);
  };

  const LEVELS = [
    { id: "hidden", label: "مخفي", icon: EyeOff, cls: "text-slate-500", active: "bg-slate-200 text-slate-700" },
    { id: "view", label: "عرض", icon: Eye, cls: "text-sky-600", active: "bg-sky-100 text-sky-700 border-sky-300" },
    { id: "edit", label: "تعديل", icon: Pencil, cls: "text-emerald-600", active: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  ];

  return (
    <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
      <button type="button" data-testid={`module-perms-toggle-${user.user_id || user.email}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-sm font-semibold text-indigo-800">
        <span className="flex items-center gap-1.5">
          <SlidersHorizontal className="h-4 w-4 text-indigo-600" /> الأقسام المسموحة
          {count > 0 && <span className="text-xs font-normal rounded-full bg-indigo-600 text-white px-2 py-0.5">مخصّص: {count}</span>}
          {count === 0 && <span className="text-xs font-normal text-slate-500">(افتراضي حسب الدور)</span>}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          <ActionPerms user={user} onSaved={onSaved} />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-slate-500 max-w-md">
              حدّد ما يراه هذا المستخدم. إن لم تحدّد أي قسم، يرى الأقسام الافتراضية حسب دوره. "عرض" = مشاهدة فقط، "تعديل" = صلاحية كاملة.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`perms-editall-${user.user_id || user.email}`} onClick={editAll} disabled={saving}>تحديد كل الصلاحيات</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`perms-showall-${user.user_id || user.email}`} onClick={showAll} disabled={saving}>إظهار الكل</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`perms-clear-${user.user_id || user.email}`} onClick={clearAll} disabled={saving}>مسح (افتراضي)</Button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto pr-1 space-y-3">
            {PERM_GROUPS.map(({ node, keys }) => {
              const gid = node ? node.id : "other";
              const allEdit = keys.every((k) => perms[k] === "edit");
              return (
                <div key={gid} data-testid={`perm-group-${user.user_id || user.email}-${gid}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-bold text-indigo-700">{node ? groupLabel(node, t) : "أقسام أخرى"}</span>
                    <div className="flex gap-1">
                      <button type="button" disabled={saving} data-testid={`perm-group-editall-${user.user_id || user.email}-${gid}`}
                        onClick={() => setGroup(keys, allEdit ? "hidden" : "edit")}
                        className={`rounded-md border px-2 py-0.5 text-[11px] transition-colors ${allEdit ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}>
                        {allEdit ? "إلغاء الكل" : "تحديد الكل"}
                      </button>
                      <button type="button" disabled={saving} data-testid={`perm-group-viewall-${user.user_id || user.email}-${gid}`}
                        onClick={() => setGroup(keys, "view")}
                        className="rounded-md border border-sky-200 px-2 py-0.5 text-[11px] text-sky-600 hover:bg-sky-50 transition-colors">الكل عرض</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {keys.map((key) => {
              const cur = perms[key] || "hidden";
              return (
                <div key={key} data-testid={`perm-row-${user.user_id || user.email}-${key}`}
                  className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
                  <span className="text-sm text-slate-700 truncate">{leafLabel(key, {}, t)}</span>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {LEVELS.map((lv) => {
                      const Icon = lv.icon;
                      const isActive = cur === lv.id;
                      return (
                        <button key={lv.id} type="button" title={lv.label} disabled={saving}
                          data-testid={`perm-${user.user_id || user.email}-${key}-${lv.id}`}
                          onClick={() => setLevel(key, lv.id)}
                          className={`flex items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] transition ${isActive ? lv.active + " border" : "border-transparent " + lv.cls + " hover:bg-slate-50"}`}>
                          <Icon className="h-3 w-3" /> {lv.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
